import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";
import { queryClient } from "@/lib/query-client";
import { AuthProvider } from "@/context/auth-context";
import { CartProvider } from "@/context/cart-context";
import { RequireAuth, RequireRole } from "@/components/RoleGuard";
import { CustomerShell } from "@/components/layout/CustomerShell";

import Login from "@/pages/Login";
import StaffLogin from "@/pages/StaffLogin";
import InviteAccept from "@/pages/InviteAccept";
import CustomerHome from "@/pages/customer/Home";
import Search from "@/pages/customer/Search";
import StoreDetail from "@/pages/customer/StoreDetail";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import CustomerOrders from "@/pages/customer/Orders";
import OrderTracking from "@/pages/customer/OrderTracking";
import BecomeVendor from "@/pages/customer/BecomeVendor";
import Privacy from "@/pages/customer/Privacy";
import LegalDoc from "@/pages/legal/LegalDoc";

import VendorDashboard from "@/pages/vendor/Dashboard";
import VendorProducts from "@/pages/vendor/Products";
import VendorStore from "@/pages/vendor/Store";

import DriverDashboard from "@/pages/driver/Dashboard";
import DriverEarnings from "@/pages/driver/Earnings";

import AdminOverview from "@/pages/admin/Overview";
import AdminVendors from "@/pages/admin/Vendors";
import AdminOrders from "@/pages/admin/Orders";
import AdminUsers from "@/pages/admin/Users";
import AdminInvites from "@/pages/admin/Invites";
import AdminDrivers from "@/pages/admin/Drivers";
import AdminPromotions from "@/pages/admin/Promotions";
import AdminCategories from "@/pages/admin/Categories";
import AdminCompliance from "@/pages/admin/Compliance";
import AdminHeroStats from "@/pages/admin/HeroStats";

function CustomerRoute({ children }) {
  return (
    <RequireRole roles={["customer"]}>
      <CustomerShell>{children}</CustomerShell>
    </RequireRole>
  );
}

function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Client id is resolved at runtime from the backend so Google works on preview AND
  // deployed builds without rebaking a REACT_APP_ env var. Falls back to build-time env.
  const [clientId, setClientId] = useState(process.env.REACT_APP_GOOGLE_CLIENT_ID || null);
  const [ready, setReady] = useState(!!process.env.REACT_APP_GOOGLE_CLIENT_ID);

  useEffect(() => {
    if (ready) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/config`)
      .then((r) => r.json())
      .then((c) => setClientId(c.google_client_id || ""))
      .catch(() => setClientId(""))
      .finally(() => setReady(true));
  }, [ready]);

  const tree = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/staff-login" element={<StaffLogin />} />
              <Route path="/invite/:token" element={<InviteAccept />} />
              <Route path="/legal/:kind" element={<LegalDoc />} />

              {/* Customer */}
              <Route path="/" element={<CustomerRoute><CustomerHome /></CustomerRoute>} />
              <Route path="/search" element={<CustomerRoute><Search /></CustomerRoute>} />
              <Route path="/store/:id" element={<CustomerRoute><StoreDetail /></CustomerRoute>} />
              <Route path="/cart" element={<CustomerRoute><Cart /></CustomerRoute>} />
              <Route path="/checkout" element={<CustomerRoute><Checkout /></CustomerRoute>} />
              <Route path="/orders" element={<CustomerRoute><CustomerOrders /></CustomerRoute>} />
              <Route path="/order/:id" element={<CustomerRoute><OrderTracking /></CustomerRoute>} />
              <Route path="/become-vendor" element={<CustomerRoute><BecomeVendor /></CustomerRoute>} />
              <Route path="/privacy" element={<CustomerRoute><Privacy /></CustomerRoute>} />

              {/* Vendor */}
              <Route path="/vendor" element={<RequireRole roles={["vendor"]}><VendorDashboard /></RequireRole>} />
              <Route path="/vendor/products" element={<RequireRole roles={["vendor"]}><VendorProducts /></RequireRole>} />
              <Route path="/vendor/store" element={<RequireRole roles={["vendor"]}><VendorStore /></RequireRole>} />

              {/* Driver */}
              <Route path="/driver" element={<RequireRole roles={["driver"]}><DriverDashboard /></RequireRole>} />
              <Route path="/driver/earnings" element={<RequireRole roles={["driver"]}><DriverEarnings /></RequireRole>} />

              {/* Admin */}
              <Route path="/admin" element={<RequireRole roles={["admin"]}><AdminOverview /></RequireRole>} />
              <Route path="/admin/vendors" element={<RequireRole roles={["admin"]}><AdminVendors /></RequireRole>} />
              <Route path="/admin/orders" element={<RequireRole roles={["admin"]}><AdminOrders /></RequireRole>} />
              <Route path="/admin/users" element={<RequireRole roles={["admin"]}><AdminUsers /></RequireRole>} />
              <Route path="/admin/invites" element={<RequireRole roles={["admin"]}><AdminInvites /></RequireRole>} />
              <Route path="/admin/drivers" element={<RequireRole roles={["admin"]}><AdminDrivers /></RequireRole>} />
              <Route path="/admin/promotions" element={<RequireRole roles={["admin"]}><AdminPromotions /></RequireRole>} />
              <Route path="/admin/categories" element={<RequireRole roles={["admin"]}><AdminCategories /></RequireRole>} />
              <Route path="/admin/compliance" element={<RequireRole roles={["admin"]}><AdminCompliance /></RequireRole>} />
              <Route path="/admin/hero-stats" element={<RequireRole roles={["admin"]}><AdminHeroStats /></RequireRole>} />

              <Route path="*" element={<RequireAuth><Navigate to="/" replace /></RequireAuth>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
  if (!ready) return null;
  return clientId ? <GoogleOAuthProvider clientId={clientId}>{tree}</GoogleOAuthProvider> : tree;
}

export default App;
